import React, { useState, useEffect } from 'react';
import { 
  Users, Target, Award, Rocket, ShieldCheck, 
  Code, Globe, Zap, Heart, ArrowRight,
  TrendingUp, Users2, Building2, Lightbulb,
  Menu, X, Smartphone, Activity, Play, Star
} from 'lucide-react';
import logo from '../assest/logo.png';
import heroImg from '../assest/hero.jpg';
import ownerImg from '../assest/owner.webp';
import '../App.css';

const AboutUs = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-[Poppins] antialiased text-slate-800 bg-white selection:bg-emerald-100 selection:text-emerald-900 overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-[100] bg-white h-14 border-b border-slate-100 shadow-sm">
        <div className="max-w-[1240px] mx-auto h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8">
               <img src={logo} alt="HRMS Software" className="w-full h-full object-contain" />
             </div>
             <span className="text-base font-extrabold tracking-tight text-slate-800">HRMS<span className="text-emerald-500 font-black">Software</span></span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-slate-500 font-semibold text-[13px]">
            <a href="/" className="hover:text-emerald-500 transition-all hover:scale-105">Home</a>
            <a href="/features" className="hover:text-emerald-500 transition-all hover:scale-105">Features</a>
            <a href="/about" className="text-emerald-500 transition-all hover:scale-105 font-bold border-b-2 border-emerald-500">About Us</a>
            <a href="/solutions" className="hover:text-emerald-600 transition-all hover:scale-105">Solutions</a>
            <a 
              href="/SecureInsights.apk" 
              download="SecureInsights.apk"
              className="px-6 py-2 rounded-[6px] bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 active:scale-95 shadow-[0_10px_30px_rgba(16,185,129,0.25)]"
            >
               Download App
            </a>
          </nav>

          <button className="md:hidden p-2 text-slate-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-100 shadow-xl z-[150] py-6 px-6 md:hidden">
            <nav className="flex flex-col gap-5 text-slate-600 font-bold text-sm">
              <a href="/" onClick={() => setIsMenuOpen(false)}>Home</a>
              <a href="/features" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="/about" onClick={() => setIsMenuOpen(false)}>About Us</a>
              <a href="/solutions" onClick={() => setIsMenuOpen(false)}>Solutions</a>
              <a 
                href="/SecureInsights.apk" 
                download="SecureInsights.apk"
                className="w-full py-4 rounded-[6px] bg-emerald-600 text-white shadow-lg text-center"
              >
                Download App
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* --- About Hero Section --- */}
      <section className="relative min-h-[40vh] flex items-center pt-20 pb-8 overflow-hidden bg-[#070b14]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none opacity-30"></div>
        
        <div className="max-w-[1100px] mx-auto px-6 text-center relative z-10 w-full">
           <div className="animate-in fade-in slide-in-from-bottom-6 duration-1000">
              <div className="inline-flex items-center gap-2.5 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-6 font-bold text-[9px] tracking-[0.2em] text-emerald-400 uppercase mx-auto">
                 <Building2 size={10} className="text-emerald-500" />
                 Our Enterprise Identity
              </div>
              
              <h1 className="text-2xl md:text-4xl font-extrabold leading-tight mb-6 tracking-tighter text-white">
                Designing the <span className="text-emerald-500">Architecture</span> <br/> of Human <span className="relative inline-block text-emerald-500">Agility.<div className="absolute bottom-1.5 left-0 w-full h-1.5 bg-emerald-500/20 -z-10"></div></span>
              </h1>

              <p className="text-xs md:text-sm text-slate-400 max-w-lg mx-auto leading-relaxed font-medium opacity-90">
                 At HRMS Software, we bridge the gap between complex organizational needs and seamless digital execution. Our mission is to empower the modern workforce.
              </p>
           </div>
        </div>
      </section>

      {/* Founder Quote Section */}
      <section className="py-12 relative overflow-hidden bg-white border-b border-slate-50">
        <div className="max-w-[1000px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
           <div className="relative group max-w-[280px] mx-auto lg:mx-0">
              <div className="relative z-10 p-1 bg-gradient-to-tr from-slate-100 to-transparent rounded-[20px] border border-slate-100 shadow-lg">
                 <div className="rounded-[18px] overflow-hidden aspect-square">
                    <img 
                      src={ownerImg} 
                      alt="Abhimanyu Kumar Singh" 
                      className="w-full h-full object-cover object-top"
                    />
                 </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-emerald-500 blur-[40px] rounded-full -z-0 opacity-10"></div>
           </div>

           <div className="space-y-8">
              <div className="space-y-3">
                 <h2 className="text-[10px] font-extrabold text-emerald-500 tracking-[0.2em] uppercase">Founder's Vision</h2>
                 <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">
                   Technology as a <br/> <span className="text-emerald-500">Human Catalyst.</span>
                 </h3>
              </div>

              <div className="relative">
                 <div className="absolute -top-6 -left-4 text-emerald-100 select-none">
                    <span className="text-[60px] font-black leading-none opacity-50">“</span>
                 </div>
                 <blockquote className="text-[15px] md:text-lg font-bold text-slate-600 leading-relaxed relative z-10 italic">
                   "Technology is not just a tool; it's the catalyst for human potential. At HRMS Software, we are committed to building digital ecosystems that empower businesses to lead, innovate, and inspire."
                 </blockquote>
              </div>

              <div className="flex items-center gap-4 pt-6 border-t border-slate-50">
                 <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-white">
                    <Users size={24} />
                 </div>
                 <div>
                    <h4 className="text-lg font-extrabold text-slate-900 leading-none mb-1.5">Abhimanyu Kumar Singh</h4>
                    <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-[0.2em]">Owner & Founder • HRMS Software</p>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50 relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
           <AboutCard 
             icon={Target} 
             title="Our Mission" 
             desc="To empower every organization and individual by providing innovative digital solutions that streamline workflows and enhance productivity."
             bgColor="bg-white" iconBg="bg-blue-50" iconColor="text-blue-500"
           />
           <AboutCard 
             icon={Lightbulb} 
             title="Our Vision" 
             desc="To become a global leader in enterprise intelligence, known for creating the most intuitive and secure HR management ecosystems."
             bgColor="bg-white" iconBg="bg-emerald-50" iconColor="text-emerald-500"
           />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#070b14] py-16 border-t border-white/5">
         <div className="max-w-[1240px] mx-auto px-6 flex flex-col items-center justify-center text-center gap-10">
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-xl bg-emerald-500/10 p-2 border border-emerald-500/20">
                  <img src={logo} alt="Logo" className="w-full h-full object-contain filter brightness-0 invert" />
               </div>
               <span className="text-xl font-black text-white tracking-tight uppercase">HRMS SOFTWARE</span>
            </div>
            
            <p className="text-[10px] md:text-[11px] font-bold text-slate-500 uppercase tracking-[0.4em] max-w-[280px] md:max-w-none leading-relaxed">
               © 2026 HRMS SOFTWARE — ALL RIGHTS RESERVED
            </p>
            
            <div className="flex items-center gap-8 md:gap-12 text-[11px] font-black text-slate-400">
               <a href="/" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Home</a>
               <a href="/about" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110 text-emerald-500">About Us</a>
               <a href="/features" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Software</a>
               <a href="/solutions" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Solutions</a>
            </div>
         </div>
      </footer>
    </div>
  );
};

const AboutCard = ({ icon: Icon, title, desc, bgColor, iconBg, iconColor }) => (
  <div className={`${bgColor} p-8 rounded-[20px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-700 group`}>
     <div className={`w-12 h-12 rounded-[16px] ${iconBg} ${iconColor} flex items-center justify-center mb-6 transition-transform group-hover:scale-110`}>
        <Icon size={24} strokeWidth={2.5} />
     </div>
     <h4 className="text-lg font-extrabold text-slate-900 mb-3">{title}</h4>
     <p className="text-[13px] text-slate-500 leading-relaxed font-medium opacity-80">{desc}</p>
  </div>
);

export default AboutUs;
